import React, { useEffect, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/utils";

const SIZES = {
  sm: { frame: "h-12 w-12", sizes: "48px" },
  md: { frame: "h-16 w-16", sizes: "64px" },
  xl: { frame: "h-40 w-40 sm:h-48 sm:w-48", sizes: "(min-width: 640px) 192px, 160px" },
  hero: { frame: "h-64 w-64 sm:h-72 sm:w-72 lg:h-80 lg:w-80", sizes: "(min-width: 1024px) 320px, (min-width: 640px) 288px, 256px" },
};

// WP-15: width variants generated by scripts/generate_icon_variants.py sit beside each source
// (fees.webp -> fees.w64.webp ...). A 512px asset was serving 48px renders — ~8x the pixels
// needed, 115+ times over. onError on the <img> still covers a missing variant set (the browser
// falls back through srcset candidates and finally the Lucide chip).
const VARIANT_WIDTHS = [64, 128, 192, 384];
function srcSetFor(src) {
  if (typeof src !== "string" || !src.endsWith(".webp")) return undefined;
  const base = src.slice(0, -".webp".length);
  return [...VARIANT_WIDTHS.map((w) => `${base}.w${w}.webp ${w}w`), `${src} 512w`].join(", ");
}

export default function Module3DIcon({ Icon, src, color, size = "md", animated = false, motionDisabled = false, ai = false, className, label, showSemanticBadge = true }) {
  const reduce = useReducedMotion();
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [src]);
  const s = SIZES[size] || SIZES.md;
  const canMove = animated && !reduce && !motionDisabled;
  const showImage = Boolean(src && !imageFailed);
  return (
    <span className={cn("module-3d-scene relative inline-grid shrink-0 place-items-center", s.frame, className)}
      style={{ "--module-3d-color": color }} aria-hidden={label ? undefined : "true"}
      role={label ? "img" : undefined} aria-label={label} data-ai={ai ? "true" : "false"}>
      <m.span className="module-3d-render absolute inset-0 block"
        initial={canMove ? { opacity: 0, transform: "translate3d(0,6px,0)" } : false}
        animate={{ opacity: 1, transform: "translate3d(0,0,0)" }}
        transition={{ duration: canMove ? 0.24 : 0, ease: [0.23, 1, 0.32, 1] }}>
        {showImage && <img src={src} srcSet={srcSetFor(src)} sizes={s.sizes} alt="" width="512" height="512" loading={["xl", "hero"].includes(size) ? "eager" : "lazy"}
          fetchPriority={["xl", "hero"].includes(size) ? "high" : "auto"} decoding="async" onError={() => setImageFailed(true)} className="h-full w-full object-contain" />}
        {!showImage && Icon && <span className="absolute inset-[15%] grid place-items-center rounded-[28%] border border-line bg-surface shadow-card" data-testid="module-3d-fallback">
          <Icon className="h-[44%] w-[44%]" style={{ color }} strokeWidth={1.7} aria-hidden="true" />
        </span>}
        {showImage && Icon && showSemanticBadge && <span className="module-3d-semantic absolute bottom-0 right-0 grid h-[34%] w-[34%] place-items-center rounded-full border border-line bg-surface shadow-sm">
          <Icon className="h-[52%] w-[52%]" style={{ color }} strokeWidth={1.9} />
        </span>}
      </m.span>
    </span>
  );
}
